import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Breadcrumbs,
  Tooltip,
  Badge,
  Grid
} from '@material-ui/core';
import { 
  Star, 
  StarBorder, 
  StarHalf, 
  Delete, 
  Refresh, 
  Visibility,
  Warning,
  TrendingUp,
  AssessmentOutlined
} from '@material-ui/icons';
import { http, FxDateDisplay, useNavigationService, localstorage, AlertService } from '../../fx-core';
import './ReviewModeration.scss';

const PAGE_SIZE_DEFAULT = 10;

const ReviewModeration = (props: any) => {
  const { routeData, back } = useNavigationService();
  const [reviews, setReviews] = useState<any[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE_DEFAULT);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportDialogData, setReportDialogData] = useState<any>(null);

  const communityId = routeData?.data?.communityId || 0;
  const communityName = routeData?.data?.communityName || 'Community';
  const currentUserId = Number(localstorage.getItem('userId')) || 0;
  const isAdmin = currentUserId === 11373 || currentUserId === 11374;

  useEffect(() => {
    fetchReportedReviews();
    // eslint-disable-next-line
  }, [communityId]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = reviews.length;
    const totalReports = reviews.reduce((sum, r) => sum + (r.reportCount || 0), 0);
    const avgReportsPerReview = total > 0 ? (totalReports / total).toFixed(1) : '0';
    const highPriority = reviews.filter(r => (r.reportCount || 0) >= 3).length;
    
    return {
      totalReviews: total,
      totalReports,
      avgReportsPerReview,
      highPriority
    };
  }, [reviews]);

  // ensure sorted by reportCount desc (server already sorts but this is defensive)
  const normalized = useMemo(() => {
    const arr = (reviews || []).map((r: any) => ({
      id: r.id,
      orderId: r.orderId,
      stallId: r.stallId,
      stallName: r.stallName,
      userId: r.userId,
      userName: r.userName,
      rating: Number(r.rating || 0),
      title: r.title,
      text: r.text,
      createdDate: r.createdDate,
      reportCount: Number(r.reportCount || (Array.isArray(r.reports) ? r.reports.length : 0)),
      reports: r.reports || []
    }));
    return arr.sort((a: any, b: any) => {
      if (b.reportCount !== a.reportCount) return b.reportCount - a.reportCount;
      const bt = new Date(b.createdDate || 0).getTime();
      const at = new Date(a.createdDate || 0).getTime();
      return bt - at;
    });
  }, [reviews]);

  const shown = useMemo(() => normalized.slice(0, visibleCount), [normalized, visibleCount]);

  async function fetchReportedReviews() {
    setLoading(true);
    try {
      const apiOptions: any = { url: 'stallreviews/reported' };
      const res = await http.get(apiOptions);
      if (res && res.data && res.data.success) {
        setReviews(res.data.reviews || []);
        setVisibleCount(PAGE_SIZE_DEFAULT);
      } else {
        // try fallback
        const data = res && res.data;
        if (Array.isArray(data)) setReviews(data);
        else {
          setReviews([]);
          if (data && data.error) AlertService.showMsg({ message: data.error });
        }
      }
    } catch (err) {
      console.error('fetchReportedReviews error', err);
      AlertService.showMsg({ message: 'Failed to fetch reported reviews' });
    } finally {
      setLoading(false);
    }
  }

  const openDeleteDialog = (id: number) => {
    setSelectedReviewId(id);
    setDeleteDialogOpen(true);
  };

  const handleAdminDelete = async () => {
    if (!selectedReviewId) return;
    try {
      const apiOptions: any = { url: `stallreviews/${selectedReviewId}` };
      const res = await http.delete(apiOptions);
      if (res && res.data && res.data.success) {
        setReviews(prev => prev.filter(r => r.id !== selectedReviewId));
        AlertService.showSuccessMsg('Review deleted successfully');
      } else {
        // fallback if status OK
        if (!res || (res.status && res.status >= 400)) throw new Error('Delete failed');
        setReviews(prev => prev.filter(r => r.id !== selectedReviewId));
        AlertService.showSuccessMsg('Review deleted');
      }
    } catch (err) {
      console.error('delete error', err);
      AlertService.showMsg({ message: 'Failed to delete review' });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedReviewId(null);
    }
  };

  const openViewReports = (r: any) => {
    setReportDialogData(r);
    setReportDialogOpen(true);
  };

  const aggregateReasons = (reports: any[]) => {
    const map = new Map<string, number>();
    for (const rep of reports || []) {
      const reason = (rep.reportReason || rep.ReportReason || 'Other').trim() || 'Other';
      map.set(reason, (map.get(reason) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5;
    for (let i = 0; i < full; i++) stars.push(<Star key={`f${i}`} className="star-filled" />);
    if (half) stars.push(<StarHalf key="half" className="star-filled" />);
    const empty = 5 - Math.ceil(rating);
    for (let i = 0; i < empty; i++) stars.push(<StarBorder key={`e${i}`} className="star-empty" />);
    return stars;
  };

  const getSeverityColor = (count: number) => {
    if (count >= 5) return '#d32f2f'; // Red
    if (count >= 3) return '#f57c00'; // Orange
    return '#ff6b6b'; // Light red
  };

  const navBack = () => back();

  return (
    <Box className="review-moderation-container">
      <div className="title-font custom-head">
        <Breadcrumbs aria-label="breadcrumb">
          <span className="custom-link pointer breadcrumbs-style" onClick={navBack}>
            <b>Dashboard</b>
          </span>
          <Typography color="textPrimary" className="breadcrumbs-style">
            Review Moderation - {communityName}
          </Typography>
        </Breadcrumbs>
      </div>

      <Box className="content-container px-4">
        <Box display="flex" justifyContent="space-between" alignItems="center" className="page-header">
          <div>
            <Typography variant="h5" className="page-title">
              🛡️ Reported Reviews Moderation
            </Typography>
            <Typography variant="body2">
              Reviews flagged by community members - sorted by severity
            </Typography>
          </div>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Refresh data">
              <Button 
                startIcon={<Refresh />} 
                onClick={() => fetchReportedReviews()} 
                variant="outlined"
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {/* Statistics Summary */}
        {!loading && normalized.length > 0 && (
          <Box className="stats-summary">
            <Card className="stat-card">
              <Typography className="stat-label">Total Flagged</Typography>
              <Typography className="stat-value">{stats.totalReviews}</Typography>
            </Card>
            <Card className="stat-card" style={{ borderLeftColor: '#f57c00' }}>
              <Typography className="stat-label">Total Reports</Typography>
              <Typography className="stat-value" style={{ color: '#f57c00' }}>
                {stats.totalReports}
              </Typography>
            </Card>
            <Card className="stat-card" style={{ borderLeftColor: '#4caf50' }}>
              <Typography className="stat-label">Avg Reports/Review</Typography>
              <Typography className="stat-value" style={{ color: '#4caf50' }}>
                {stats.avgReportsPerReview}
              </Typography>
            </Card>
            <Card className="stat-card" style={{ borderLeftColor: '#d32f2f' }}>
              <Typography className="stat-label">High Priority (3+)</Typography>
              <Typography className="stat-value" style={{ color: '#d32f2f' }}>
                {stats.highPriority}
              </Typography>
            </Card>
          </Box>
        )}

        <Box className="reviews-list mt-3">
          {loading && (
            <Box className="no-reviews-box">
              <Typography variant="h6">Loading reviews...</Typography>
            </Box>
          )}

          {!loading && normalized.length === 0 ? (
            <Box className="no-reviews-box">
              <Typography variant="h6" color="textSecondary">
                ✅ No reported reviews
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Great! There are no flagged reviews at the moment.
              </Typography>
            </Box>
          ) : (
            <>
              {shown.map((r: any) => {
                const userName = r.userName || (r.userId ? `User #${r.userId}` : 'Unknown');
                const stallName = r.stallName || (r.stallId ? `Stall #${r.stallId}` : '—');
                const reasons = aggregateReasons(r.reports);
                const severityColor = getSeverityColor(r.reportCount);
                const isHighPriority = r.reportCount >= 3;

                return (
                  <Card key={r.id} className="review-card">
                    <CardContent>
                      <Box className="review-header">
                        <Box display="flex" gap={2} alignItems="center">
                          <Badge
                            badgeContent={r.reportCount}
                            color="error"
                            overlap="circle"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          >
                            <Avatar className="user-avatar">
                              {(userName || 'U').toString().slice(0, 2).toUpperCase()}
                            </Avatar>
                          </Badge>
                          
                          <Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography className="user-name">{userName}</Typography>
                              <Box className="inline-stars">{renderStars(r.rating)}</Box>
                              {isHighPriority && (
                                <Tooltip title="High Priority - Multiple reports">
                                  <Warning style={{ color: '#f57c00', fontSize: 20 }} />
                                </Tooltip>
                              )}
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                              Order: <strong>{r.orderId || '—'}</strong> • Stall: <strong>{stallName}</strong>
                            </Typography>
                          </Box>
                        </Box>

                        <Box className="review-actions-top">
                          <Chip 
                            label={`${r.reportCount} ${r.reportCount === 1 ? 'Report' : 'Reports'}`}
                            size="small" 
                            className="chip-reports"
                            style={{ background: severityColor }}
                          />
                          
                          {reasons.slice(0, 2).map(([reason, cnt]: any, idx: number) => (
                            <Tooltip key={idx} title={`${reason}: ${cnt} report${cnt > 1 ? 's' : ''}`}>
                              <Chip 
                                label={`${reason} ×${cnt}`}
                                size="small" 
                                variant="outlined" 
                                className="chip-reason"
                              />
                            </Tooltip>
                          ))}

                          <Tooltip title="View all reports">
                            <IconButton 
                              size="small" 
                              onClick={() => openViewReports(r)}
                              style={{ color: '#667eea' }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>

                          {isAdmin && (
                            <Tooltip title="Delete review permanently">
                              <IconButton 
                                size="small" 
                                onClick={() => openDeleteDialog(r.id)} 
                                className="delete-icon-button"
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Typography variant="caption" className="review-date">
                            <FxDateDisplay value={r.createdDate} />
                          </Typography>
                        </Box>
                      </Box>

                      {r.title && (
                        <Typography variant="subtitle1" className="review-title">
                          {r.title}
                        </Typography>
                      )}
                      
                      <Typography variant="body2" className="review-text">
                        {r.text}
                      </Typography>

                      {/* Show top reason in a highlighted box */}
                      {reasons.length > 0 && (
                        <Box className="report-reason-box">
                          <Typography variant="caption" className="report-label">
                            Most Common Reason
                          </Typography>
                          <Typography variant="body2" className="report-reason-text">
                            {reasons[0][0]} ({reasons[0][1]} {reasons[0][1] === 1 ? 'report' : 'reports'})
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}

              {normalized.length > shown.length && (
                <Box className="load-more-box">
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={() => setVisibleCount(c => c + PAGE_SIZE_DEFAULT)}
                    size="large"
                  >
                    Load More Reviews
                  </Button>
                  <Typography variant="caption" color="textSecondary">
                    Showing <strong>{shown.length}</strong> of <strong>{normalized.length}</strong> reported reviews
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning style={{ color: '#f57c00' }} />
            Delete Review Permanently
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            This action cannot be undone. The review will be permanently removed from the system.
          </Typography>
          <Typography style={{ marginTop: 12, color: '#666', fontSize: 14 }}>
            Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleAdminDelete} 
            color="secondary" 
            variant="contained"
            startIcon={<Delete />}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Reports Dialog */}
      <Dialog 
        open={reportDialogOpen} 
        onClose={() => setReportDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AssessmentOutlined />
            Report Details
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {!reportDialogData ? (
            <Typography>No report data available</Typography>
          ) : (
            <>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Review Content
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {reportDialogData.title || 'No title'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {reportDialogData.text || '—'}
                </Typography>
                <Box mt={1} display="flex" alignItems="center" gap={1}>
                  {renderStars(reportDialogData.rating)}
                  <Typography variant="caption" color="textSecondary">
                    ({reportDialogData.rating.toFixed(1)} stars)
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 700 }}>
                  Reports ({reportDialogData.reports?.length || 0})
                </Typography>
                
                {reportDialogData.reports && reportDialogData.reports.length > 0 ? (
                  <Grid container spacing={2} style={{ marginTop: 8 }}>
                    {reportDialogData.reports.map((rep: any, idx: number) => {
                      const reporterName = rep.reportedByUserName || 
                        (rep.reportedByUserId ? `User #${rep.reportedByUserId}` : 'Anonymous');
                      
                      return (
                        <Grid item xs={12} key={idx}>
                          <Card variant="outlined" style={{ padding: 12, background: '#f9f9f9' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                  {reporterName}
                                </Typography>
                                <Chip 
                                  label={rep.reportReason || 'Other'} 
                                  size="small" 
                                  style={{ 
                                    marginTop: 4, 
                                    background: '#fff', 
                                    border: '1px solid #ddd' 
                                  }} 
                                />
                              </Box>
                              <Typography variant="caption" color="textSecondary">
                                <FxDateDisplay value={rep.reportedDate} />
                              </Typography>
                            </Box>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No detailed reports available for this review.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewModeration;